import { createAsyncThunk } from "@reduxjs/toolkit"

import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import { rejectWithHandledError } from "../../../utils/handlers/reject-with-handled-error.ts"
import { rejectPendingCommitments } from "../../Projects/api/pending-commitments.ts"
import { isArchivingStatus } from "../../Projects/constants/project-statuses.ts"
import * as services from "../../StatusAutomation/api/status-automation.services.ts"
import { readStatusRefusal } from "../../StatusAutomation/helpers/status-automation.ts"
import type {
  ProjectSuggestionAnswer,
  ProjectUndoAnswer,
  StatusSuggestion,
  StatusSuggestionAcceptBody
} from "../../StatusAutomation/types/status-automation.types.ts"
import { getProject } from "../api/project.services.ts"
import type { Project } from "../types/project.types.ts"
import { projectSliceName } from "./project.constants.ts"

interface SuggestionArgs {
  projectId: string
  suggestionId: string
}

export type ProjectAcceptAnswer = ProjectSuggestionAnswer & {
  /** Re-read after a close, which archives the project as well as moving it. */
  project?: Project
}

export const acceptProjectStatusSuggestion = createAsyncThunk<
  ProjectAcceptAnswer,
  SuggestionArgs & { body?: StatusSuggestionAcceptBody }
>(
  `${projectSliceName}/acceptStatusSuggestion`,
  async ({ projectId, suggestionId, body }, { dispatch, rejectWithValue }) => {
    const response = await services.acceptStatusSuggestion(
      "project",
      projectId,
      suggestionId,
      body
    )

    if (!response.ok) {
      const refusal = readStatusRefusal(response.data)
      if (response.status === 409 && refusal) return rejectWithValue(refusal)
      return (
        rejectPendingCommitments(response.data, rejectWithValue) ??
        rejectWithHandledError(response, {
          dispatch,
          rejectWithValue,
          flow: "project.acceptStatusSuggestion"
        })
      )
    }

    const answer = unwrapEnvelope<ProjectSuggestionAnswer>(response.data)
    if (!isArchivingStatus(answer?.project_status)) return answer

    const reread = await getProject(projectId)
    return reread.ok
      ? { ...answer, project: reread.data as Project }
      : answer
  }
)

export const dismissProjectStatusSuggestion = createAsyncThunk<
  { status_suggestions: StatusSuggestion[] },
  SuggestionArgs
>(
  `${projectSliceName}/dismissStatusSuggestion`,
  async ({ projectId, suggestionId }, { dispatch, rejectWithValue }) => {
    const response = await services.dismissStatusSuggestion(
      "project",
      projectId,
      suggestionId
    )

    if (!response.ok) {
      const refusal = readStatusRefusal(response.data)
      if (response.status === 409 && refusal) return rejectWithValue(refusal)
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "project.dismissStatusSuggestion"
      })
    }

    return unwrapEnvelope<{ status_suggestions: StatusSuggestion[] }>(
      response.data
    )
  }
)

export const undoProjectStatusTransition = createAsyncThunk<
  ProjectUndoAnswer,
  { projectId: string; transitionId: string }
>(
  `${projectSliceName}/undoStatusTransition`,
  async ({ projectId, transitionId }, { dispatch, rejectWithValue }) => {
    const response = await services.undoStatusTransition(
      "project",
      projectId,
      transitionId
    )

    if (!response.ok) {
      const refusal = readStatusRefusal(response.data)
      if (response.status === 409 && refusal) return rejectWithValue(refusal)
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "project.undoStatusTransition"
      })
    }

    return unwrapEnvelope<ProjectUndoAnswer>(response.data)
  }
)

/**
 * A quiet re-read after a status rule may have moved the open project: no
 * loading state and no error toast, since nothing the user did is waiting on it.
 */
export const refreshProjectStatus = createAsyncThunk<Project, string>(
  `${projectSliceName}/refreshStatus`,
  async (projectId, { rejectWithValue }) => {
    const response = await getProject(projectId)
    if (!response.ok || !response.data) return rejectWithValue(null)
    return unwrapEnvelope<Project>(response.data)
  }
)
